import React, { FC } from 'react';
import './FileList.css';

/**
 * Type: UploadedFile
 *
 * Represents a file item that can be either:
 * - a freshly uploaded file (File object)
 * - or an already stored file accessible via URL
 *
 * This abstraction allows the component to be reused
 * for both upload previews and existing document lists.
 */
interface UploadedFile {
	file: File;
	url?: string;
}

interface FileListProps {
	uploadedFile: UploadedFile[];
	onRemove?: (file: UploadedFile) => void;
}

/**
 * Component: FileList
 *
 * Description:
 * Generic UI component responsible for displaying a list of files.
 * It supports both local file previews and remote file access.
 *
 * The component is intentionally agnostic of:
 * - upload logic
 * - persistence
 * - domain-specific rules
 *
 * Responsibilities:
 * - Render file names with download/open links
 * - Generate temporary preview URLs for local files
 * - Optionally expose a removal action
 *
 * Design principles:
 * - Reusable and domain-agnostic
 * - Stateless and controlled by parent components
 * - Focused on presentation only
 *
 * Author: Samano CASTRE
 */
const FileList: FC<FileListProps> = ({ uploadedFile, onRemove }) => {

	/**
	 * Renders a list of file entries.
	 *
	 * Each file is displayed as a clickable link:
	 * - Local files use a generated object URL for preview
	 * - Remote files rely on their persisted URL
	 *
	 * When provided, the remove action is delegated
	 * to the parent component via callback.
	 */
	return (
		<ul className="FileList" data-testid="FileList">
			{
				uploadedFile.map(
					(uploadedFile, index) => (
						<li key={uploadedFile.url ?? index}>
							<a
								className='ellipsis'
								href={uploadedFile.url || URL.createObjectURL(uploadedFile.file)}
								target="_blank" rel="noopener noreferrer">
								{uploadedFile.file.name}
							</a>
							{
								onRemove && (
									<button onClick={() => onRemove(uploadedFile)}>
										Supprimer
									</button>
								)
							}
						</li>
					)
				)
			}
		</ul>
	);
}

export default FileList;